import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import { useSearchParams } from 'react-router-dom';
import DetailPanel from '../components/DetailPanel';
import { useAuth } from '../AuthContext';
import { OPERATIONS_TABS } from '../config/pages';

// ── Status maps ──────────────────────────────────────────────────────────────
const STATUS_MAP = {
  Draft:             { label:'In Progress',     color:'var(--accent)' },
  Rejected:          { label:'Rejected',         color:'#e84545' },
  'Pending Internal':{ label:'Internal Review', color:'#e87830' },
  'Pending Client':  { label:'Client Review',   color:'#6c58f0' },
  Approved:          { label:'Approved',         color:'var(--green)' },
};

const TAB_CONFIG = [
  { id:'design',   label:'Design',             statuses:['Draft','Rejected'],   color:'var(--accent)' },
  { id:'internal', label:'Internal Review',     statuses:['Pending Internal'],   color:'#e87830' },
  { id:'client',   label:'Client Review',       statuses:['Pending Client'],     color:'#6c58f0' },
  { id:'approved', label:'Approved',            statuses:['Approved'],           color:'var(--green)' },
];

const EMPTY_MSG = {
  design:  { icon:'🖨️', msg:'No print media in design',                sub:'Work orders with a Print Media phase unlocked will appear here.' },
  internal:{ icon:'🔒', msg:'No designs pending internal review',       sub:'Submit designs for internal review to see them here.' },
  client:  { icon:'👔', msg:'No designs pending client review',         sub:'Internally approved designs will appear here.' },
  approved:{ icon:'✅', msg:'No approved designs yet',                  sub:'Client-approved designs will appear here.' },
};

const DESIGN_TYPES = {
  brochure:       '📄 Brochure',
  visiting_card:  '💳 Visiting Card',
  package_design: '📦 Package Design',
  standee:        '🪧 Standee',
  banner:         '🏳️ Banner',
  letterhead:     '📃 Letterhead',
  poster:         '🖼️ Poster',
  flyer:          '📰 Flyer',
  other:          '📋 Other',
};

const DESIGN_STATUSES = ['Briefed','Assigned','Designing','Draft Ready'];
const DESIGN_COLORS   = {
  'Briefed':'#6b7280','Assigned':'#3b82f6','Designing':'#8b5cf6','Draft Ready':'#22c55e',
};

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

function fmtDateShort(iso) {
  if (!iso) return '—';
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { weekday:'short', day:'2-digit', month:'short', year:'numeric' });
}

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   MAIN COMPONENT
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export default function PrintMedia() {
  const { user, can, canTab } = useAuth();
  const [searchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const urlWo  = searchParams.get('wo');

  const allowedTabs = (OPERATIONS_TABS.printmedia || TAB_CONFIG).filter(
    t => !t.permissionKey || canTab(t.permissionKey)
  );

  const [tab, setTab]               = useState(urlTab && allowedTabs.find(t=>t.id===urlTab) ? urlTab : (allowedTabs[0]?.id || 'design'));
  const [items, setItems]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [activeWOId, setActiveWOId] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [urlWoHandled, setUrlWoHandled] = useState(false);

  // ── Filters ──
  const [monthFilter, setMonthFilter]         = useState('');
  const [clientFilter, setClientFilter]       = useState('');
  const [campaignFilter, setCampaignFilter]   = useState('');
  const [searchTerm, setSearchTerm]           = useState('');

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (monthFilter)    params.month            = monthFilter;
      if (clientFilter)   params.client_id        = clientFilter;
      if (campaignFilter) params.campaign_type_id  = campaignFilter;
      const r = await api.get('/print-media', { params });
      setItems(r.data.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [monthFilter, clientFilter, campaignFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-open work order from URL
  useEffect(() => {
    if (urlWoHandled || !urlWo || items.length === 0) return;
    const match = items.find(i => i.wo_number === urlWo || String(i.id) === urlWo);
    if (match) { setActiveWOId(match.id); setUrlWoHandled(true); }
  }, [items, urlWo, urlWoHandled]);

  // ── Derived data ──
  const tabCfg        = TAB_CONFIG.find(t => t.id === tab) || TAB_CONFIG[0];
  const filteredItems = items.filter(wo => {
    const pm = wo.print_media;
    const st = pm?.status || 'Draft';
    if (!tabCfg.statuses.includes(st)) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      if (!((wo.wo_number||'').toLowerCase().includes(s) ||
            (wo.title||'').toLowerCase().includes(s) ||
            (wo.client_name||'').toLowerCase().includes(s))) return false;
    }
    return true;
  });

  const activeWO = items.find(i => i.id === activeWOId);
  const activePM = activeWO?.print_media;
  const counts   = {};
  TAB_CONFIG.forEach(t => {
    counts[t.id] = items.filter(wo => t.statuses.includes(wo.print_media?.status || 'Draft')).length;
  });

  // ── Unique clients & campaign types for filters ──
  const uniqueClients   = [...new Map(items.map(i => [i.client?.id, i.client]).filter(([k]) => k)).values()];
  const uniqueCampaigns = [...new Set(items.map(i => i.campaign_type).filter(Boolean))];

  // ── CRUD helpers ──
  const saveField = async (field, value) => {
    if (!activeWOId || saving) return;
    setSaving(true);
    try {
      await api.put(`/print-media/${activeWOId}`, { [field]: value });
      await fetchData();
    } catch (e) { alert(e.response?.data?.message || 'Save failed'); }
    setSaving(false);
  };

  const saveMultiple = async (data) => {
    if (!activeWOId || saving) return;
    setSaving(true);
    try {
      await api.put(`/print-media/${activeWOId}`, data);
      await fetchData();
    } catch (e) { alert(e.response?.data?.message || 'Save failed'); }
    setSaving(false);
  };

  const doAction = async (action) => {
    if (!activeWOId) return;
    setSaving(true);
    try {
      await api.post(`/print-media/${activeWOId}/${action}`);
      await fetchData();
    } catch (e) { alert(e.response?.data?.message || 'Action failed'); }
    setSaving(false);
  };

  const doReject = async (reason, fbVoice) => {
    if (!activeWOId) return;
    setSaving(true);
    try {
      await api.post(`/print-media/${activeWOId}/reject`, { reason, fb_voice_data: fbVoice });
      await fetchData();
    } catch (e) { alert(e.response?.data?.message || 'Action failed'); }
    setSaving(false);
  };

  // ── Render ──
  return (
    <div className="page-wrap" style={{ display:'flex', gap:0, height:'calc(100vh - 56px)' }}>
      {/* ── LEFT: List ── */}
      <div style={{ flex:1, minWidth:0, display:'flex', flexDirection:'column', borderRight:'1px solid var(--border)' }}>
        {/* Tabs */}
        <div style={{ display:'flex', gap:6, padding:'12px 16px 0', flexWrap:'wrap' }}>
          {allowedTabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{
                padding:'6px 14px', borderRadius:8, fontSize:'0.78rem', fontWeight:600,
                border: tab===t.id ? `2px solid ${t.color}` : '1px solid var(--border)',
                background: tab===t.id ? t.color+'18' : 'var(--bg2)',
                color: tab===t.id ? t.color : 'var(--text-muted)',
                cursor:'pointer',
              }}>
              {t.icon} {t.label}
              <span style={{ marginLeft:6, fontSize:'0.7rem', opacity:0.7 }}>({counts[t.id]||0})</span>
            </button>
          ))}
        </div>

        {/* Search & filters */}
        <div style={{ padding:'10px 16px', display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
          <input placeholder="Search WO, title, client..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            style={{ flex:1, minWidth:160, padding:'6px 10px', borderRadius:6, border:'1px solid var(--border)',
              background:'var(--bg)', color:'var(--text)', fontSize:'0.8rem' }} />
          <select className="form-select" value={clientFilter} onChange={e => setClientFilter(e.target.value)}
            style={{ fontSize:'0.78rem', padding:'5px 8px' }}>
            <option value="">All Clients</option>
            {uniqueClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input type="month" value={monthFilter} onChange={e => setMonthFilter(e.target.value)}
            style={{ fontSize:'0.78rem', padding:'5px 8px', borderRadius:6, border:'1px solid var(--border)',
              background:'var(--bg)', color:'var(--text)' }} />
        </div>

        {/* WO list */}
        <div style={{ flex:1, overflowY:'auto', padding:'0 16px 16px' }}>
          {loading ? (
            <div style={{ textAlign:'center', padding:40, color:'var(--text-muted)' }}>Loading...</div>
          ) : filteredItems.length === 0 ? (
            <div className="card" style={{ padding:40, textAlign:'center', color:'var(--text-muted)' }}>
              <div style={{ fontSize:'2rem', marginBottom:8 }}>{EMPTY_MSG[tab]?.icon || '🖨️'}</div>
              <div style={{ fontSize:'0.9rem', fontWeight:600 }}>{EMPTY_MSG[tab]?.msg}</div>
              <div style={{ fontSize:'0.8rem', marginTop:4 }}>{EMPTY_MSG[tab]?.sub}</div>
            </div>
          ) : (
            filteredItems.map(wo => (
              <WOCard key={wo.id} wo={wo} active={activeWOId===wo.id} onClick={() => setActiveWOId(wo.id)} />
            ))
          )}
        </div>
      </div>

      {/* ── RIGHT: Detail panel ── */}
      <div style={{ width:activeWOId ? 480 : 0, transition:'width 0.2s', overflow:'hidden', flexShrink:0 }}>
        {activeWO && (
          <PrintMediaDetailPanel
            wo={activeWO}
            pm={activePM}
            user={user}
            can={can}
            saving={saving}
            onSaveField={saveField}
            onSaveMultiple={saveMultiple}
            onAction={doAction}
            onReject={doReject}
            onClose={() => setActiveWOId(null)}
            onRefresh={fetchData}
          />
        )}
      </div>
    </div>
  );
}

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   WO LIST CARD
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
function WOCard({ wo, active, onClick }) {
  const pm   = wo.print_media;
  const st   = STATUS_MAP[pm?.status || 'Draft'] || STATUS_MAP.Draft;
  const design = pm?.design_status || 'Briefed';
  const type = DESIGN_TYPES[pm?.design_type || 'brochure'] || '📄 Brochure';

  return (
    <div className="card" onClick={onClick} style={{
      padding:'14px 16px', marginBottom:8, cursor:'pointer',
      borderLeft: active ? '3px solid var(--accent)' : '3px solid transparent',
      background: active ? 'var(--accent-light, rgba(99,102,241,0.06))' : undefined,
      transition:'all 0.15s',
    }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
            <span style={{ fontFamily:'Space Mono, monospace', fontSize:'0.8rem', fontWeight:700, color:'var(--accent)' }}>
              {wo.wo_number}
            </span>
            <span style={{ fontSize:'0.84rem', fontWeight:600, color:'var(--text)' }}>{wo.title}</span>
          </div>
          <div style={{ fontSize:'0.76rem', color:'var(--text-muted)', marginTop:4, display:'flex', gap:8, flexWrap:'wrap' }}>
            <span>{wo.client_name || '—'}</span>
            {wo.event_date && <span>· {fmtDateShort(wo.event_date)}</span>}
            <span>· {type}</span>
          </div>
        </div>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4, flexShrink:0 }}>
          <span style={{
            fontSize:'0.7rem', fontWeight:700, padding:'2px 8px', borderRadius:6,
            background: st.color + '18', color: st.color, whiteSpace:'nowrap',
          }}>{st.label}</span>
          <span style={{
            fontSize:'0.66rem', fontWeight:600, padding:'2px 6px', borderRadius:4,
            background: (DESIGN_COLORS[design]||'#6b7280') + '18', color: DESIGN_COLORS[design]||'#6b7280',
          }}>{design}</span>
        </div>
      </div>
    </div>
  );
}

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   DETAIL PANEL
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
function PrintMediaDetailPanel({ wo, pm, user, can, saving, onSaveField, onSaveMultiple, onAction, onReject, onClose, onRefresh }) {
  const [editMode, setEditMode]     = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Local form state
  const [form, setForm] = useState({});

  useEffect(() => {
    if (pm) {
      setForm({
        design_type:    pm.design_type || 'brochure',
        brief:          pm.brief || '',
        concept:        pm.concept || '',
        size:           pm.size || '',
        orientation:    pm.orientation || '',
        paper_type:     pm.paper_type || '',
        quantity:       pm.quantity || '',
        specifications: pm.specifications || '',
        external_name:  pm.external_name || '',
        external_phone: pm.external_phone || '',
        external_email: pm.external_email || '',
        draft_link:     pm.draft_link || '',
        final_link:     pm.final_link || '',
        print_file_link:pm.print_file_link || '',
        notes:          pm.notes || '',
        design_status:  pm.design_status || 'Briefed',
      });
    } else {
      setForm({ design_type:'brochure', brief:'', concept:'', size:'', orientation:'', paper_type:'',
        quantity:'', specifications:'', external_name:'', external_phone:'', external_email:'',
        draft_link:'', final_link:'', print_file_link:'', notes:'', design_status:'Briefed' });
    }
    setEditMode(!pm);
    setRejectMode(false);
    setRejectReason('');
  }, [pm, wo.id]);

  const handleSave = () => {
    const data = { ...form };
    if (data.quantity === '') delete data.quantity;
    else data.quantity = parseInt(data.quantity, 10) || 0;
    onSaveMultiple(data);
    setEditMode(false);
  };

  const status       = pm?.status || 'Draft';
  const canApproveInt = can('action:approve:printmedia:internal');
  const canApproveCl  = can('action:approve:printmedia:client');
  const isClient      = user?.role === 'client';
  const showEdit      = !isClient && ['Draft','Rejected'].includes(status);

  const designStatus = pm?.design_status || 'Briefed';
  const designIdx    = DESIGN_STATUSES.indexOf(designStatus);

  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column', background:'var(--bg)' }}>
      {/* Header */}
      <div style={{
        padding:'14px 16px', borderBottom:'1px solid var(--border)',
        display:'flex', justifyContent:'space-between', alignItems:'center',
      }}>
        <div>
          <span style={{ fontFamily:'Space Mono, monospace', fontSize:'0.82rem', fontWeight:700, color:'var(--accent)' }}>
            {wo.wo_number}
          </span>
          <span style={{ fontSize:'0.88rem', fontWeight:600, color:'var(--text)', marginLeft:8 }}>{wo.title}</span>
        </div>
        <button onClick={onClose} style={{
          background:'none', border:'none', fontSize:'1.2rem', cursor:'pointer', color:'var(--text-muted)', padding:'4px 8px',
        }}>✕</button>
      </div>

      {/* Content */}
      <div style={{ flex:1, overflowY:'auto', padding:16 }}>
        {/* Status badges */}
        <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
          <StatusBadge label={STATUS_MAP[status]?.label || status} color={STATUS_MAP[status]?.color || '#666'} />
          <StatusBadge label={designStatus} color={DESIGN_COLORS[designStatus] || '#666'} />
          <StatusBadge label={DESIGN_TYPES[pm?.design_type || form.design_type] || 'Brochure'} color="#8b5cf6" />
        </div>

        {/* Design progress bar */}
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:'0.74rem', fontWeight:700, color:'var(--text)', marginBottom:6 }}>Design Progress</div>
          <div style={{ display:'flex', gap:2 }}>
            {DESIGN_STATUSES.map((ds, i) => (
              <div key={ds} title={ds} style={{
                flex:1, height:6, borderRadius:3,
                background: i <= designIdx ? (DESIGN_COLORS[ds]||'#6b7280') : 'var(--border)',
                transition:'background 0.3s',
              }} />
            ))}
          </div>
          <div style={{ fontSize:'0.68rem', color:'var(--text-muted)', marginTop:3 }}>
            {designIdx + 1} of {DESIGN_STATUSES.length} — {designStatus}
          </div>
        </div>

        {/* WO Info */}
        <Section title="Work Order">
          <InfoRow label="Client" value={wo.client_name} />
          <InfoRow label="Event Date" value={fmtDateShort(wo.event_date)} />
          <InfoRow label="Campaign" value={wo.campaign_type} />
        </Section>

        {/* Brief & Concept */}
        {editMode ? (
          <Section title="Brief & Design Type">
            <Label>Design Type</Label>
            <select className="form-select" value={form.design_type}
              onChange={e => setForm({...form, design_type:e.target.value})}
              style={{ fontSize:'0.8rem', marginBottom:8 }}>
              {Object.entries(DESIGN_TYPES).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <Label>Brief</Label>
            <textarea rows={3} value={form.brief} onChange={e => setForm({...form, brief:e.target.value})}
              style={taStyle} placeholder="Describe what needs to be designed..." />
            <Label>Concept / Style</Label>
            <textarea rows={2} value={form.concept} onChange={e => setForm({...form, concept:e.target.value})}
              style={taStyle} placeholder="Visual style, color scheme, mood..." />
          </Section>
        ) : (
          <Section title="Brief & Design Type">
            <InfoRow label="Design Type" value={DESIGN_TYPES[pm?.design_type] || '—'} />
            {pm?.brief && <InfoRow label="Brief" value={pm.brief} />}
            {pm?.concept && <InfoRow label="Concept" value={pm.concept} />}
          </Section>
        )}

        {/* Specifications */}
        {editMode ? (
          <Section title="Specifications">
            <div style={{ display:'flex', gap:8 }}>
              <div style={{ flex:1 }}>
                <Label>Size</Label>
                <input value={form.size} onChange={e => setForm({...form, size:e.target.value})}
                  style={inStyle} placeholder="e.g. A4, 3x5 inch" />
              </div>
              <div style={{ flex:1 }}>
                <Label>Orientation</Label>
                <select className="form-select" value={form.orientation}
                  onChange={e => setForm({...form, orientation:e.target.value})}
                  style={{ fontSize:'0.8rem' }}>
                  <option value="">— Select —</option>
                  <option value="portrait">Portrait</option>
                  <option value="landscape">Landscape</option>
                  <option value="square">Square</option>
                </select>
              </div>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <div style={{ flex:1 }}>
                <Label>Paper Type</Label>
                <input value={form.paper_type} onChange={e => setForm({...form, paper_type:e.target.value})}
                  style={inStyle} placeholder="e.g. 300gsm art card" />
              </div>
              <div style={{ flex:1 }}>
                <Label>Quantity</Label>
                <input type="number" value={form.quantity} onChange={e => setForm({...form, quantity:e.target.value})}
                  style={inStyle} placeholder="Print quantity" min="0" />
              </div>
            </div>
            <Label>Additional Specifications</Label>
            <textarea rows={2} value={form.specifications} onChange={e => setForm({...form, specifications:e.target.value})}
              style={taStyle} placeholder="Finishing, coating, folding, binding..." />
          </Section>
        ) : (
          (pm?.size || pm?.orientation || pm?.paper_type || pm?.quantity || pm?.specifications) && (
            <Section title="Specifications">
              {pm?.size && <InfoRow label="Size" value={pm.size} />}
              {pm?.orientation && <InfoRow label="Orientation" value={pm.orientation} />}
              {pm?.paper_type && <InfoRow label="Paper Type" value={pm.paper_type} />}
              {pm?.quantity && <InfoRow label="Quantity" value={pm.quantity} />}
              {pm?.specifications && <InfoRow label="Details" value={pm.specifications} />}
            </Section>
          )
        )}

        {/* Assignment */}
        {editMode ? (
          <Section title="Assignment (External)">
            <Label>Designer Name</Label>
            <input value={form.external_name} onChange={e => setForm({...form, external_name:e.target.value})}
              style={inStyle} placeholder="Name" />
            <div style={{ display:'flex', gap:8 }}>
              <div style={{ flex:1 }}>
                <Label>Phone</Label>
                <input value={form.external_phone} onChange={e => setForm({...form, external_phone:e.target.value})}
                  style={inStyle} placeholder="Phone" />
              </div>
              <div style={{ flex:1 }}>
                <Label>Email</Label>
                <input value={form.external_email} onChange={e => setForm({...form, external_email:e.target.value})}
                  style={inStyle} placeholder="Email" />
              </div>
            </div>
          </Section>
        ) : (
          (pm?.assigned_user || pm?.external_name) && (
            <Section title="Assignment">
              {pm?.assigned_user && <InfoRow label="In-house" value={pm.assigned_user} />}
              {pm?.external_name && <InfoRow label="Designer" value={pm.external_name} />}
              {pm?.external_phone && <InfoRow label="Phone" value={pm.external_phone} />}
              {pm?.external_email && <InfoRow label="Email" value={pm.external_email} />}
            </Section>
          )
        )}

        {/* Delivery Links */}
        {editMode ? (
          <Section title="Delivery Links">
            <Label>Draft Design Link</Label>
            <input value={form.draft_link} onChange={e => setForm({...form, draft_link:e.target.value})}
              style={inStyle} placeholder="Google Drive / Dropbox link" />
            <Label>Final Design Link</Label>
            <input value={form.final_link} onChange={e => setForm({...form, final_link:e.target.value})}
              style={inStyle} placeholder="Final approved design link" />
            <Label>Print-Ready File Link</Label>
            <input value={form.print_file_link} onChange={e => setForm({...form, print_file_link:e.target.value})}
              style={inStyle} placeholder="High-res PDF for printing" />
          </Section>
        ) : (
          (pm?.draft_link || pm?.final_link || pm?.print_file_link) && (
            <Section title="Delivery Links">
              {pm?.draft_link && <LinkRow label="Draft Design" url={pm.draft_link} />}
              {pm?.final_link && <LinkRow label="Final Design" url={pm.final_link} />}
              {pm?.print_file_link && <LinkRow label="Print File" url={pm.print_file_link} />}
            </Section>
          )
        )}

        {/* Design Status (edit mode) */}
        {editMode && (
          <Section title="Design Status">
            <select className="form-select" value={form.design_status}
              onChange={e => setForm({...form, design_status:e.target.value})}
              style={{ fontSize:'0.8rem' }}>
              {DESIGN_STATUSES.map(ds => <option key={ds} value={ds}>{ds}</option>)}
            </select>
          </Section>
        )}

        {/* Notes */}
        {editMode ? (
          <Section title="Notes">
            <textarea rows={3} value={form.notes} onChange={e => setForm({...form, notes:e.target.value})}
              style={taStyle} placeholder="Internal notes..." />
          </Section>
        ) : (
          pm?.notes && <Section title="Notes"><div style={{ fontSize:'0.82rem', color:'var(--text)', whiteSpace:'pre-wrap' }}>{pm.notes}</div></Section>
        )}

        {/* References */}
        {pm?.references?.length > 0 && !editMode && (
          <Section title="References">
            {pm.references.map((ref, i) => (
              <div key={i} style={{ marginBottom:4 }}>
                {typeof ref === 'string' ? (
                  <a href={ref} target="_blank" rel="noreferrer" style={{ fontSize:'0.8rem', color:'var(--accent)', wordBreak:'break-all' }}>{ref}</a>
                ) : ref.url ? (
                  <a href={ref.url} target="_blank" rel="noreferrer" style={{ fontSize:'0.8rem', color:'var(--accent)', wordBreak:'break-all' }}>{ref.label || ref.url}</a>
                ) : (
                  <span style={{ fontSize:'0.8rem', color:'var(--text)' }}>{ref.label || JSON.stringify(ref)}</span>
                )}
              </div>
            ))}
          </Section>
        )}

        {/* Rejection reason */}
        {pm?.rejection_reason && (
          <Section title="Revision Feedback">
            <div style={{ padding:10, background:'#fef2f2', borderRadius:8, border:'1px solid #fecaca', fontSize:'0.82rem', color:'#991b1b' }}>
              {pm.rejection_reason}
            </div>
          </Section>
        )}

        {/* Version history */}
        {pm?.versions?.length > 0 && (
          <Section title="Version History">
            {[...pm.versions].reverse().map((v, i) => (
              <div key={i} style={{ padding:'6px 0', borderBottom:'1px solid var(--border)', fontSize:'0.78rem' }}>
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <span style={{ fontWeight:600 }}>{v.version} — {v.action}</span>
                  <span style={{ color:'var(--text-muted)' }}>{fmtDate(v.at)}</span>
                </div>
                <div style={{ color:'var(--text-muted)' }}>by {v.by}{v.stage ? ` (${v.stage})` : ''}</div>
                {v.rejectionReason && <div style={{ color:'#e84545', marginTop:2 }}>Reason: {v.rejectionReason}</div>}
              </div>
            ))}
          </Section>
        )}

        {/* Reject panel */}
        {rejectMode && (
          <Section title="Request Revision">
            <textarea rows={3} value={rejectReason} onChange={e => setRejectReason(e.target.value)}
              style={taStyle} placeholder="Describe what needs to change..." />
            <div style={{ display:'flex', gap:8, marginTop:8 }}>
              <button className="btn" onClick={() => { onReject(rejectReason, null); setRejectMode(false); setRejectReason(''); }}
                disabled={saving} style={{ background:'#e84545', color:'#fff', fontSize:'0.8rem' }}>
                {saving ? 'Sending...' : 'Send Revision Request'}
              </button>
              <button className="btn" onClick={() => setRejectMode(false)}
                style={{ background:'var(--bg2)', color:'var(--text)', fontSize:'0.8rem' }}>Cancel</button>
            </div>
          </Section>
        )}
      </div>

      {/* Footer actions */}
      <div style={{ padding:'12px 16px', borderTop:'1px solid var(--border)', display:'flex', gap:8, flexWrap:'wrap' }}>
        {showEdit && !editMode && (
          <button className="btn" onClick={() => setEditMode(true)}
            style={{ background:'var(--accent)', color:'#fff', fontSize:'0.8rem' }}>Edit</button>
        )}
        {editMode && (
          <>
            <button className="btn" onClick={handleSave} disabled={saving}
              style={{ background:'var(--accent)', color:'#fff', fontSize:'0.8rem' }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            {pm && <button className="btn" onClick={() => setEditMode(false)}
              style={{ background:'var(--bg2)', color:'var(--text)', fontSize:'0.8rem' }}>Cancel</button>}
          </>
        )}
        {!editMode && showEdit && pm && (
          <button className="btn" onClick={() => onAction('submit-internal')} disabled={saving}
            style={{ background:'#e87830', color:'#fff', fontSize:'0.8rem' }}>
            Submit for Review
          </button>
        )}
        {status === 'Pending Internal' && canApproveInt && !rejectMode && (
          <>
            <button className="btn" onClick={() => onAction('approve-internal')} disabled={saving}
              style={{ background:'var(--green)', color:'#fff', fontSize:'0.8rem' }}>Approve (Internal)</button>
            <button className="btn" onClick={() => setRejectMode(true)}
              style={{ background:'#e84545', color:'#fff', fontSize:'0.8rem' }}>Request Revision</button>
          </>
        )}
        {status === 'Pending Client' && canApproveCl && !rejectMode && (
          <>
            <button className="btn" onClick={() => onAction('approve-client')} disabled={saving}
              style={{ background:'var(--green)', color:'#fff', fontSize:'0.8rem' }}>Approve (Client)</button>
            <button className="btn" onClick={() => setRejectMode(true)}
              style={{ background:'#e84545', color:'#fff', fontSize:'0.8rem' }}>Request Revision</button>
          </>
        )}
      </div>
    </div>
  );
}

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   UI HELPERS
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
function Section({ title, children }) {
  return (
    <div style={{ marginBottom:16 }}>
      <div style={{ fontSize:'0.74rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:8 }}>{title}</div>
      {children}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display:'flex', gap:8, padding:'3px 0', fontSize:'0.82rem' }}>
      <span style={{ color:'var(--text-muted)', minWidth:90, flexShrink:0 }}>{label}:</span>
      <span style={{ color:'var(--text)', whiteSpace:'pre-wrap', wordBreak:'break-word' }}>{value || '—'}</span>
    </div>
  );
}

function LinkRow({ label, url }) {
  return (
    <div style={{ display:'flex', gap:8, padding:'4px 0', fontSize:'0.82rem', alignItems:'center' }}>
      <span style={{ color:'var(--text-muted)', minWidth:90, flexShrink:0 }}>{label}:</span>
      <a href={url} target="_blank" rel="noreferrer" style={{ color:'var(--accent)', wordBreak:'break-all', display:'flex', alignItems:'center', gap:4 }}>
        🔗 {url.length > 50 ? url.substring(0, 50) + '...' : url}
      </a>
    </div>
  );
}

function StatusBadge({ label, color }) {
  return (
    <span style={{
      fontSize:'0.72rem', fontWeight:700, padding:'3px 10px', borderRadius:6,
      background: color + '18', color,
    }}>{label}</span>
  );
}

function Label({ children }) {
  return <div style={{ fontSize:'0.74rem', fontWeight:600, color:'var(--text-muted)', marginBottom:3, marginTop:8 }}>{children}</div>;
}

const inStyle = {
  width:'100%', padding:'7px 10px', borderRadius:6, border:'1px solid var(--border)',
  background:'var(--bg)', color:'var(--text)', fontSize:'0.82rem', marginBottom:4,
};

const taStyle = {
  ...inStyle, resize:'vertical', fontFamily:'inherit',
};
